import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ClipboardList,
  CalendarDays,
  CheckCircle2,
  ArrowUpRight,
  ExternalLink,
  Clock,
  Video,
  ChevronDown,
} from "lucide-react";
import type { ComponentType, SVGProps, ReactNode } from "react";
import { todayPanelTz } from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { AttendeeList, type Attendee } from "@/components/AttendeeList";
import { PlaneRefreshHeader } from "@/components/PlaneRefreshHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

interface DashboardData {
  today: string;
  hasStandupToday: boolean;
  standupId: string | null;
  recentStandups: { id: string; date: string; preview: string[] }[];
  myProjects: {
    id: string;
    name: string;
    slug: string;
    status: string;
    health: string;
    type: string;
    roleInProject: string;
  }[];
  myPendingDaysOff: {
    id: string;
    startDate: string;
    endDate: string;
    kind: string;
  }[];
  pendingApprovals: {
    id: string;
    requesterName: string;
    startDate: string;
    endDate: string;
    kind: string;
  }[];
  pendingEvaluations: {
    id: string;
    level: "client" | "organizational" | "team";
    period: string;
    templateName: string;
  }[];
  tickets: {
    overdue: TicketLite[];
    upcoming: TicketLite[];
    open: TicketLite[];
  };
  ticketsMeta: { cachedAt: number; source: "manual" | "auto" } | null;
}

interface TicketLite {
  id: string;
  name: string;
  state: string;
  stateGroup?: "backlog" | "unstarted" | "started" | "completed" | "cancelled" | "unknown";
  priority: string;
  dueDate?: string;
  url: string;
  projectId?: string;
  projectName?: string;
  projectIdentifier?: string;
  panelProjectId?: string;
  panelProjectName?: string;
  panelProjectSlug?: string;
}

// Colores por GRUPO de estado (enum estable de Plane). El nombre lo
// renderiza tal cual viene del proyecto ("Histórico", "Listo para revisar",
// etc.) — los colores los decidimos por grupo para no depender del nombre.
const STATE_BADGE_BY_GROUP: Record<NonNullable<TicketLite["stateGroup"]>, string> = {
  backlog: "badge-ghost",
  unstarted: "badge-info",
  started: "badge-primary",
  completed: "badge-success",
  cancelled: "badge-warning",
  unknown: "badge-ghost",
};

// Para las métricas resumen arriba de los buckets: orden de presentación
// + color de punto + label corto en español. Completed/cancelled se
// excluyen porque la API ya los filtra de los buckets activos.
const STATE_METRIC_ORDER: Array<NonNullable<TicketLite["stateGroup"]>> = [
  "started",
  "unstarted",
  "backlog",
  "unknown",
];
const STATE_METRIC: Record<
  NonNullable<TicketLite["stateGroup"]>,
  { label: string; dot: string }
> = {
  backlog: { label: "Backlog", dot: "bg-base-content/35" },
  unstarted: { label: "Por iniciar", dot: "bg-info" },
  started: { label: "En curso", dot: "bg-primary" },
  completed: { label: "Listos", dot: "bg-success" },
  cancelled: { label: "Cancelados", dot: "bg-warning" },
  unknown: { label: "Otros", dot: "bg-base-content/25" },
};

interface CalendarEventLite {
  id: string;
  kind: string;
  date: string;
  endDate?: string;
  title: string;
  url?: string;
  attendees?: Attendee[];
  meta?: Record<string, string>;
}

export const Route = createFileRoute("/")({
  component: DashboardPage,
});

function DashboardPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ["dashboard", "me"],
    queryFn: () => api.get("/dashboard/me"),
  });
  const refreshTickets = useMutation({
    mutationFn: () => api.get<DashboardData>("/dashboard/me?refresh=1"),
    onSuccess: (fresh) => qc.setQueryData(["dashboard", "me"], fresh),
  });

  // Auto-refresh único cuando la primera respuesta llega sin tickets.
  // Cubre el caso en que la cache de Plane está stale tras un sync fallido
  // del cron. Si el segundo pull también devuelve vacío, no insistimos —
  // hay empleados que efectivamente no tienen tickets.
  const autoRefreshedRef = useRef(false);
  useEffect(() => {
    if (!data || autoRefreshedRef.current) return;
    const total =
      data.tickets.overdue.length + data.tickets.upcoming.length + data.tickets.open.length;
    if (total === 0 && !refreshTickets.isPending) {
      autoRefreshedRef.current = true;
      refreshTickets.mutate();
    }
  }, [data, refreshTickets]);
  // Eventos del calendario para la card "Próximos eventos". Comparten
  // endpoint con la vista /calendar para mantener una sola fuente de
  // verdad. Filtramos tickets en el cliente — esos ya viven en su card.
  const { data: calendarData } = useQuery<{ events: CalendarEventLite[] }>({
    queryKey: ["calendar", "me"],
    queryFn: () => api.get("/calendar/me"),
  });
  const upcomingEvents = useMemo(() => {
    if (!calendarData?.events) return [];
    const todayIso = todayPanelTz();
    return calendarData.events
      .filter((e) => e.kind !== "ticket" && e.date >= todayIso)
      .toSorted((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return (a.meta?.startTime ?? "").localeCompare(b.meta?.startTime ?? "");
      });
  }, [calendarData?.events]);

  if (isLoading || !data) {
    return (
      <div className="grid place-items-center py-20">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  const ticketCount =
    data.tickets.overdue.length + data.tickets.upcoming.length + data.tickets.open.length;

  // Métricas por estado: tickets pueden aparecer en más de un bucket
  // (un atrasado también es abierto), así que deduplicamos por id antes
  // de contar. Resultado: visión real de "cuántos en cada estado".
  const stateCounts = (() => {
    const seen = new Set<string>();
    const counts: Partial<Record<NonNullable<TicketLite["stateGroup"]>, number>> = {};
    for (const t of [...data.tickets.overdue, ...data.tickets.upcoming, ...data.tickets.open]) {
      if (seen.has(t.id)) continue;
      seen.add(t.id);
      const sg = t.stateGroup ?? "unknown";
      counts[sg] = (counts[sg] ?? 0) + 1;
    }
    return counts;
  })();

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="hoy"
        title="Tu día en Axorial"
        subtitle="Lo que necesitas mirar primero. Resumen vivo de tu trabajo, proyectos y aprobaciones pendientes."
      />

      {/* Banner "Evaluaciones pendientes" pausado — ver EVALUATIONS_PAUSED.md.
      {data.pendingEvaluations.length > 0 && (
        <Banner
          tone="warning"
          icon={ClipboardList}
          title={`${data.pendingEvaluations.length} evaluación${data.pendingEvaluations.length > 1 ? "es" : ""} pendiente${data.pendingEvaluations.length > 1 ? "s" : ""} de responder`}
          description="Tu input ayuda al ciclo de mejora continua."
          action={
            <Link to="/evaluations" search={{ status: "open" }} className="btn btn-warning btn-sm">
              Responder
            </Link>
          }
        />
      )}
      */}

      {/* Aprobaciones pendientes (lead/admin) */}
      {data.pendingApprovals.length > 0 && (
        <Banner
          tone="warning"
          icon={Clock}
          title={`${data.pendingApprovals.length} solicitud${data.pendingApprovals.length > 1 ? "es" : ""} de día libre por aprobar`}
          description="Tus reportes te están esperando."
          action={
            <Link to="/days-off" className="btn btn-warning btn-sm">
              Revisar
            </Link>
          }
        />
      )}

      {/* Grid principal: la columna grande apila Standups + Tickets;
          la sidebar derecha apila Eventos + Proyectos. En móvil/tablet
          todo stackea verticalmente. */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 items-start">
        {/* ───── Columna principal: Standups + Tickets ───── */}
        <div className="xl:col-span-3 space-y-4">
          <StandupsCard
            recent={data.recentStandups ?? []}
            today={data.today}
            hasToday={data.hasStandupToday}
            todayId={data.standupId}
          />
          <section className="card-elevated p-4 md:p-5">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70 inline-flex items-center gap-2">
                <span className="size-1.5 rounded-full bg-primary" aria-hidden />
                Tus tickets
                <span className="text-base-content/45 font-normal normal-case tracking-normal">
                  · {ticketCount}
                </span>
              </h3>
              <div className="flex items-center gap-3 flex-wrap">
                <PlaneRefreshHeader
                  meta={data.ticketsMeta}
                  isPending={refreshTickets.isPending}
                  onRefresh={() => refreshTickets.mutate()}
                />
                <Link
                  to="/tickets"
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                  Ver todos <ArrowUpRight className="size-3" />
                </Link>
                <Link
                  to="/calendar"
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                  Calendario <ArrowUpRight className="size-3" />
                </Link>
              </div>
            </div>
            {/* Métricas por estado — visión rápida de en qué etapa están
              tus tickets. Suma del enum estable (stateGroup), no del
              nombre del estado que cada proyecto tunea. */}
            {ticketCount > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-4 pb-3 border-b border-base-300/60">
                {STATE_METRIC_ORDER.map((sg) => {
                  const count = stateCounts[sg] ?? 0;
                  if (!count) return null;
                  const m = STATE_METRIC[sg];
                  return (
                    <span
                      key={sg}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-base-200/60 text-xs"
                    >
                      <span className={`size-1.5 rounded-full ${m.dot}`} aria-hidden />
                      <span className="text-base-content/70">{m.label}</span>
                      <span className="font-bold tabular-nums">{count}</span>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-4">
              <TicketBucket
                label="Atrasados"
                tone="error"
                tickets={data.tickets.overdue}
                empty="🎉 Sin atrasados"
                loading={refreshTickets.isPending}
              />
              <TicketBucket
                label="Próximos · 3 días"
                tone="warning"
                tickets={data.tickets.upcoming}
                empty="Nada inminente"
                loading={refreshTickets.isPending}
              />
              <TicketBucket
                label="Abiertos"
                tone="primary"
                tickets={data.tickets.open}
                empty="Inbox limpio"
                loading={refreshTickets.isPending}
              />
            </div>
          </section>
        </div>

        {/* ───── Sidebar derecha: Eventos + Proyectos ───── */}
        <div className="xl:col-span-2 space-y-4">
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70 inline-flex items-center gap-2">
                <span className="size-1.5 rounded-full bg-success" aria-hidden />
                Próximos eventos
              </h3>
              <Link
                to="/calendar"
                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              >
                Calendario <ArrowUpRight className="size-3" />
              </Link>
            </div>
            <UpcomingEvents events={upcomingEvents} />
          </section>

          {data.myProjects.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70 inline-flex items-center gap-2">
                  <span className="size-1.5 rounded-full bg-info" aria-hidden />
                  Proyectos
                </h3>
                <Link
                  to="/projects"
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                  Ver todos <ArrowUpRight className="size-3" />
                </Link>
              </div>
              <ul className="card-elevated divide-y divide-base-300/60">
                {data.myProjects.map((p) => (
                  <li key={p.id}>
                    <Link
                      to="/projects/$projectId"
                      params={{ projectId: p.id }}
                      className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-base-200/60 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <HealthDot health={p.health} />
                        <div className="min-w-0">
                          <div className="font-medium text-base-content truncate text-sm">
                            {p.name}
                          </div>
                          <div className="text-[11px] text-base-content/55 capitalize inline-flex items-center gap-1.5 flex-wrap">
                            <span className="badge badge-ghost badge-xs">
                              {p.type === "client" ? "cliente" : "interno"}
                            </span>
                            <span>{p.status}</span>
                            <span className="text-primary">· {p.roleInProject}</span>
                          </div>
                        </div>
                      </div>
                      <ArrowUpRight className="size-4 text-base-content/40 shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>

      {/* TODO: Sección "Noticias" — placeholder. Iría aquí, abajo del
          grid principal y arriba de días libres. Mostraría posts cortos
          del equipo (anuncios, lecturas recomendadas, links). Pendiente
          definir modelo (tabla `news` propia vs. relay de Mattermost) y
          permisos de quién puede publicar (probablemente admin + lead). */}

      {/* Mis días libres pendientes */}
      {data.myPendingDaysOff.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70 mb-3">
            Tus solicitudes de días libres pendientes
          </h3>
          <ul className="card-elevated divide-y divide-base-300/60">
            {data.myPendingDaysOff.map((r) => (
              <li key={r.id} className="px-5 py-3.5 flex items-center gap-3 text-sm">
                <CalendarDays className="size-4 text-base-content/40 shrink-0" />
                <span className="flex-1 truncate">
                  {r.startDate === r.endDate
                    ? formatDate(r.startDate)
                    : `${formatDate(r.startDate)} → ${formatDate(r.endDate)}`}{" "}
                  · <span className="text-base-content/60">{r.kind}</span>
                </span>
                <span className="badge badge-warning badge-sm">Pendiente</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ─── Components ───────────────────────────────────────────────────────────

function Banner({
  tone,
  icon: Icon,
  title,
  description,
  action,
}: {
  tone: "success" | "warning" | "error";
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
  action: ReactNode;
}) {
  const tones = {
    success: "border-l-success bg-success/5",
    warning: "border-l-warning bg-warning/5",
    error: "border-l-error bg-error/5",
  };
  const iconTones = {
    success: "text-success",
    warning: "text-warning",
    error: "text-error",
  };
  return (
    <div className={`card-elevated p-4 flex items-start gap-3 border-l-4 ${tones[tone]}`}>
      <Icon className={`size-5 shrink-0 mt-0.5 ${iconTones[tone]}`} />
      <div className="flex-1 min-w-0">
        <div className="font-medium">{title}</div>
        <div className="text-sm text-base-content/65 mt-0.5">{description}</div>
      </div>
      {action}
    </div>
  );
}

/**
 * Card "Standups" arriba de Tickets. Tres columnas con los últimos
 * standups del usuario (uno por columna) + un slot para el CTA del día:
 *   - Si ya hizo el standup de hoy, muestra una pill verde "Hoy listo".
 *   - Si no, una pill ámbar con botón "Hacer standup".
 *
 * El CTA del día reemplaza al banner que vivía arriba — la idea es que
 * el standup sea una "tira de tres" con su acción contextual, no un
 * tope de página separado del resto del trabajo.
 */
function StandupsCard({
  recent,
  today,
  hasToday,
  todayId,
}: {
  recent: { id: string; date: string; preview: string[] }[];
  today: string;
  hasToday: boolean;
  todayId: string | null;
}) {
  // Asegurar que el slot de hoy sea el primero. Si recent ya trae el
  // standup de hoy (porque hasToday=true), lo aislamos para no duplicar.
  const past = recent.filter((s) => s.date !== today).slice(0, 2);
  const todayPreview = recent.find((s) => s.date === today)?.preview ?? [];
  // Las 3 columnas: [hoy, anterior, anterior-2]. Si no hay suficientes
  // anteriores, dejamos placeholders vacíos para mantener el grid.
  return (
    <section className="card-elevated p-4 md:p-5">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70 inline-flex items-center gap-2">
          <span className="size-1.5 rounded-full bg-accent" aria-hidden />
          Standups
        </h3>
        <Link
          to="/standups"
          className="text-xs text-primary hover:underline inline-flex items-center gap-1"
        >
          Ver todos <ArrowUpRight className="size-3" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Slot de hoy */}
        {hasToday && todayId ? (
          <Link
            to="/standups/$standupId"
            params={{ standupId: todayId }}
            className="rounded-lg border border-success/40 bg-success/5 p-3 hover:bg-success/10 transition-colors flex flex-col gap-1.5"
          >
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-success">
              <CheckCircle2 className="size-3.5" />
              Hoy
            </span>
            <span className="text-sm font-medium">{formatDate(today)}</span>
            {todayPreview.length > 0 ? (
              <ul className="text-xs text-base-content/70 space-y-0.5 mt-0.5">
                {todayPreview.map((line, i) => (
                  <li key={i} className="line-clamp-1">
                    · {line}
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-xs text-base-content/55">
                Listo · tu equipo ya sabe en qué andas
              </span>
            )}
          </Link>
        ) : (
          <div className="rounded-lg border border-warning/40 bg-warning/5 p-3 flex flex-col gap-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-warning">
              <ClipboardList className="size-3.5" />
              Hoy
            </span>
            <span className="text-sm font-medium">No has hecho tu standup</span>
            <span className="text-xs text-base-content/65">
              2 minutos. Tu equipo necesita saber en qué andas.
            </span>
            <Link to="/standups/new" className="btn btn-warning btn-xs mt-auto self-start">
              Hacer standup
            </Link>
          </div>
        )}
        {/* Standups anteriores */}
        {[0, 1].map((i) => {
          const s = past[i];
          if (!s) {
            return (
              <div
                key={`empty-${i}`}
                className="rounded-lg border border-base-300/60 border-dashed p-3 text-xs text-base-content/40 italic flex items-center justify-center min-h-[88px]"
              >
                Sin standup previo
              </div>
            );
          }
          return (
            <Link
              key={s.id}
              to="/standups/$standupId"
              params={{ standupId: s.id }}
              className="rounded-lg border border-base-300/60 p-3 hover:bg-base-200/60 transition-colors flex flex-col gap-1.5"
            >
              <span className="text-[11px] font-semibold uppercase tracking-wider text-base-content/55">
                Anterior
              </span>
              <span className="text-sm font-medium">{formatDate(s.date)}</span>
              {s.preview.length > 0 ? (
                <ul className="text-xs text-base-content/60 space-y-0.5 mt-0.5">
                  {s.preview.map((line, i) => (
                    <li key={i} className="line-clamp-1">
                      · {line}
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-xs text-base-content/55 inline-flex items-center gap-1">
                  Ver detalle <ArrowUpRight className="size-3" />
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function TicketBucket({
  label,
  tone,
  tickets,
  empty,
  loading = false,
}: {
  label: string;
  tone: "primary" | "warning" | "error";
  tickets: TicketLite[];
  empty: string;
  /** Cuando refrescamos los tickets pinchamos cada bucket con un
   *  spinner para que el user sepa que se está sincronizando, no
   *  solo el botón "Actualizar" arriba. */
  loading?: boolean;
}) {
  const accent = {
    primary: "bg-primary",
    warning: "bg-warning",
    error: "bg-error",
  }[tone];
  const SHOW_LIMIT = 4;
  return (
    <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
      <div className="flex items-center justify-between pb-2 border-b border-base-300/60">
        <div className="flex items-center gap-2">
          <span className={`size-2 rounded-full ${accent}`} aria-hidden />
          <h4 className="text-xs font-semibold uppercase tracking-wider text-base-content/70">
            {label}
          </h4>
          {loading && <span className="loading loading-spinner loading-xs text-base-content/55" />}
        </div>
        <span className="text-sm font-bold tabular-nums">{tickets.length}</span>
      </div>
      {tickets.length === 0 ? (
        <p className="pt-3 text-sm text-base-content/45 italic">{empty}</p>
      ) : (
        <ul className="divide-y divide-base-300/40">
          {tickets.slice(0, SHOW_LIMIT).map((t) => (
            <li key={t.id} className="py-2.5 flex items-center gap-2 text-sm">
              <a
                href={t.url}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 flex-1 hover:text-primary transition-colors"
              >
                <div className="truncate">{t.name}</div>
                <div className="text-xs text-base-content/55 mt-0.5 flex items-center gap-1.5 flex-wrap">
                  {t.panelProjectName ? (
                    // Mapeado al panel: badge primary (no "ghost") y
                    // tooltip mostrando el nombre en Plane por trazabilidad.
                    <span
                      className="badge badge-primary badge-xs"
                      title={`En Plane: ${t.projectName ?? "—"}`}
                    >
                      {t.panelProjectName}
                    </span>
                  ) : (
                    t.projectName && (
                      <span
                        className="badge badge-ghost badge-xs"
                        title={t.projectIdentifier ?? undefined}
                      >
                        {t.projectName}
                      </span>
                    )
                  )}
                  {t.state && (
                    <span
                      className={`badge badge-xs ${STATE_BADGE_BY_GROUP[t.stateGroup ?? "unknown"]}`}
                      title={`Estado: ${t.state}`}
                    >
                      {t.state}
                    </span>
                  )}
                  {t.dueDate && <span>vence {formatDate(t.dueDate)}</span>}
                </div>
              </a>
              <a
                href={t.url}
                target="_blank"
                rel="noreferrer"
                className="text-base-content/30 hover:text-primary shrink-0"
                aria-label="Abrir en Plane"
              >
                <ExternalLink className="size-3" />
              </a>
            </li>
          ))}
          {tickets.length > SHOW_LIMIT && (
            <li className="pt-2">
              <Link
                to="/tickets"
                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              >
                Ver todos (+{tickets.length - SHOW_LIMIT})
                <ArrowUpRight className="size-3" />
              </Link>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/** Etiqueta corta tipo "vie 22 may" para listas densas. */
function formatDateShortDow(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const [, y, mo, d] = m;
  return new Date(parseInt(y!, 10), parseInt(mo!, 10) - 1, parseInt(d!, 10))
    .toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" })
    .replace(/\./g, "");
}

/** Devuelve cuántos días hay entre `iso` y hoy. 0 = hoy, 1 = mañana, etc. */
function daysFromToday(iso: string): number {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return 999;
  const [, y, mo, d] = m;
  const eventDay = new Date(parseInt(y!, 10), parseInt(mo!, 10) - 1, parseInt(d!, 10));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((eventDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function relativeDayLabel(iso: string): string {
  const d = daysFromToday(iso);
  if (d === 0) return "Hoy";
  if (d === 1) return "Mañana";
  if (d < 7) return "Esta semana";
  if (d < 14) return "Próxima semana";
  return "Más adelante";
}

/**
 * Card "Próximos eventos": lista cronológica agrupada por proximidad.
 * Cada evento es un acordeón individual — click en el header lo expande
 * y muestra notas, attendees, Meet y datos de sesión. Múltiples pueden
 * estar abiertos a la vez. Por defecto el primero (más cercano) viene
 * expandido para mantener el comportamiento de "ya veo el de hoy" sin
 * obligar a un click.
 */
function UpcomingEvents({ events }: { events: CalendarEventLite[] }) {
  // Set de ids expandidos. Lo inicializamos en lazy state con el primer
  // evento para que el dashboard arranque con el más cercano abierto.
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(events[0] ? [events[0].id] : []),
  );
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (events.length === 0) {
    return (
      <div className="card-elevated p-6 text-sm text-base-content/55 text-center">
        <CalendarDays className="size-8 mx-auto mb-2 opacity-30" />
        Sin eventos próximos.
      </div>
    );
  }

  const VISIBLE_LIMIT = 8;
  const visible = events.slice(0, VISIBLE_LIMIT);
  const overflow = events.length - visible.length;

  // Agrupado por etiqueta relativa; preserva el orden cronológico
  // dentro de cada grupo (ya viene sorted desde el caller).
  const groups: { label: string; items: CalendarEventLite[] }[] = [];
  for (const e of visible) {
    const label = relativeDayLabel(e.date);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(e);
    else groups.push({ label, items: [e] });
  }

  return (
    <div className="card-elevated overflow-hidden">
      {groups.map((g, i) => (
        <div key={i} className={i > 0 ? "border-t border-base-300/60" : ""}>
          <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-base-content/45 bg-base-200/40">
            {g.label}
          </div>
          {g.items.map((e) => (
            <ExpandableEvent
              key={e.id}
              event={e}
              isOpen={expanded.has(e.id)}
              onToggle={() => toggle(e.id)}
            />
          ))}
        </div>
      ))}
      {overflow > 0 && (
        <div className="px-4 py-2.5 border-t border-base-300/60">
          <Link
            to="/calendar"
            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
          >
            Ver +{overflow} más en el calendario
            <ArrowUpRight className="size-3" />
          </Link>
        </div>
      )}
    </div>
  );
}

/**
 * Item de "Próximos eventos" colapsable. Header siempre visible (fecha,
 * hora, título + chevron). Cuerpo expandible con notas, info de sesión,
 * attendees y acciones (Meet, Calendar, etc).
 */
function ExpandableEvent({
  event,
  isOpen,
  onToggle,
}: {
  event: CalendarEventLite;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const startTime = event.meta?.startTime;
  const endTime = event.meta?.endTime;
  const notes = event.meta?.notes;
  const meetUrl = event.meta?.meetUrl;
  const htmlLink = event.meta?.htmlLink;
  const isGoogle = event.meta?.source === "google_calendar";
  const sessionDescription = event.meta?.sessionDescription;
  const sessionMuralUrl = event.meta?.sessionMuralUrl;
  const hasBody =
    !!notes ||
    !!sessionDescription ||
    !!sessionMuralUrl ||
    (event.attendees?.length ?? 0) > 0 ||
    !!meetUrl ||
    !!htmlLink ||
    !!event.url;

  return (
    <div className={isOpen ? "bg-base-200/20" : ""}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-2.5 flex items-center gap-3 text-sm text-left hover:bg-base-200/30 transition-colors"
        aria-expanded={isOpen}
      >
        <span
          className={`size-1.5 rounded-full shrink-0 ${isGoogle ? "bg-success" : "bg-info"}`}
          aria-hidden
        />
        <div className="min-w-0 flex-1 flex items-center gap-2">
          <span className="text-[11px] text-base-content/50 tabular-nums shrink-0">
            {formatDateShortDow(event.date)}
            {startTime && <span className="ml-1 text-base-content/75">{startTime}</span>}
          </span>
          <span
            className={"truncate " + (isOpen ? "text-primary font-medium" : "text-base-content/90")}
          >
            {event.title}
          </span>
        </div>
        {meetUrl && !isOpen && (
          // Atajo a Meet cuando el item está colapsado — evita tener que
          // abrir para clickear. Cuando está expandido, el botón grande
          // de Meet vive abajo en la sección de acciones.
          <a
            href={meetUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-success hover:text-success-content shrink-0 p-1"
            title="Unirse a Meet"
          >
            <Video className="size-3.5" />
          </a>
        )}
        {hasBody && (
          <ChevronDown
            className={
              "size-3.5 text-base-content/45 shrink-0 transition-transform " +
              (isOpen ? "rotate-180" : "")
            }
            aria-hidden
          />
        )}
      </button>

      {isOpen && hasBody && (
        <div className="px-4 pb-3 pt-1 space-y-2.5 border-l-2 border-primary/30 ml-3">
          {endTime && endTime !== startTime && (
            <div className="text-[11px] text-base-content/55">
              <span className="tabular-nums font-medium text-base-content/80">
                {startTime}–{endTime}
              </span>
            </div>
          )}
          {notes && (
            <p className="text-sm text-base-content/70 whitespace-pre-wrap leading-relaxed line-clamp-4">
              {notes}
            </p>
          )}
          {(sessionDescription || sessionMuralUrl) && (
            <div className="rounded-md bg-primary/8 border border-primary/15 px-3 py-2 space-y-1.5 text-xs">
              <div className="text-[10px] uppercase tracking-wider text-primary/85 font-semibold">
                De la sesión
              </div>
              {sessionDescription && (
                <p className="text-base-content/80 whitespace-pre-wrap leading-snug line-clamp-3">
                  {sessionDescription}
                </p>
              )}
              {sessionMuralUrl && (
                <a
                  href={sessionMuralUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-primary hover:underline"
                >
                  <ExternalLink className="size-3" /> Abrir Mural
                </a>
              )}
            </div>
          )}
          {event.attendees && event.attendees.length > 0 && (
            <AttendeeList attendees={event.attendees} compact />
          )}
          <div className="flex flex-wrap gap-2 pt-0.5">
            {meetUrl && (
              <a href={meetUrl} target="_blank" rel="noreferrer" className="btn btn-success btn-sm">
                <Video className="size-3.5" /> Unirse a Meet
              </a>
            )}
            {isGoogle && htmlLink && (
              <a href={htmlLink} target="_blank" rel="noreferrer" className="btn btn-ghost btn-xs">
                Calendar <ExternalLink className="size-3" />
              </a>
            )}
            {!isGoogle && event.url && (
              <a
                href={event.url}
                target="_blank"
                rel="noreferrer"
                className="btn btn-primary btn-sm"
              >
                Abrir <ExternalLink className="size-3.5" />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const HEALTH_RING: Record<string, string> = {
  green: "bg-success ring-success/30",
  yellow: "bg-warning ring-warning/30",
  red: "bg-error ring-error/30",
  unknown: "bg-base-300 ring-base-300/30",
};

function HealthDot({ health }: { health: string }) {
  return (
    <span
      aria-hidden
      title={`Salud: ${health}`}
      className={`size-2.5 rounded-full ring-4 ${HEALTH_RING[health] ?? HEALTH_RING.unknown} shrink-0`}
    />
  );
}
